import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import useMediaQuery from '@material-ui/core/useMediaQuery'

// constants
import { wholeContainerStyle, mainColumnStyle, mainContainerStyle } from '@/constants/layout'

// components
import { Header } from '@/components/organisms/cleaner/header'
import { LoadingFull } from '@/components/molecules/loading-full'
import { ListCleaningRoom } from '@/components/organisms/cleaning-manager/list-cleaning-room'
import { DayStats } from '@/components/organisms/cleaning-manager/day-stats'
import { GeneralNote } from '@/components/organisms/cleaning-manager/general-note-for-cleaner'
import { fetchCleaningManagerSetting, fetchListRoom, fetchStaffByMonth } from '@/apis/aipass'
import { AnnounceType, CheckListType, RoomsDataType, StaffType } from '@/constants/cleaning-manager'
import RoomDetail from '@/components/organisms/cleaning-manager/list-cleaning-room/room-detail-for-cleaner'
import { CleaningManagerContext } from '@/components/organisms/cleaning-manager/list-cleaning-room/room'
import { useAutoReload } from '@/hooks/use-auto-reload'
import { IndexSp } from '@/components/organisms/cleaner/sp/index-sp'
import { StaffStatsList } from '@/components/organisms/cleaning-manager/staff-stats-list'
import { useHistory } from 'react-router-dom'
import { pickTranslatedText } from '@/libs/cleaning-manager'

const queryString = require('query-string')

export const CleaningManager = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { dateOf } = queryString.parse(history.location.search)
  const { setReloadCallback } = useAutoReload()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [rooms, setRooms] = useState<RoomsDataType>({})
  const [roomStats, setRoomStats] = useState({
    totalRoomDoNotDisturb: 0,
    totalRoomNotClean: 0,
    totalRoomNotCleanWithStay: 0,
    totalRoomNotCleanWithOut: 0,
    totalRoomCleaned: 0,
    totalRoomInspected: 0,
  })
  const collapsedStorageKey = 'cleaningManager.collapsedTracking'
  const [collapsedTracking, setCollapsedTracking] = useState(JSON.parse(localStorage.getItem(collapsedStorageKey) || '{}'))
  const [selectedDate, setSelectedDate] = useState(dateOf ? dayjs(dateOf) : dayjs())
  const [isOpenRoomDetailModal, setIsOpenRoomDetailModal] = useState(false)
  const [selectedRoomId, setSelectedRoomId] = useState('')
  const [generalNote, setGeneralNote] = useState<AnnounceType>({ announce: '', announceEn: '' })
  const [storedCheckList, setStoredCheckList] = useState<CheckListType[]>([])
  const [staffList, setStaffList] = useState<StaffType[]>([])
  const [displayCleaningStaffId, setDisplayCleaningStaffId] = useState('')

  const fetchCleaningData = (fetchDate: Dayjs) => {
    setIsLoading(true)
    fetchStaffByMonth(fetchDate.format('YYYY-MM-DD')).then(data => {
      setStaffList(data)
    })
    fetchListRoom(fetchDate.format('YYYY-MM-DD'))
      .then(data => {
        setRooms(data.allGuestRooms)
        setRoomStats({
          totalRoomDoNotDisturb: data.totalRoomDoNotDisturb,
          totalRoomNotClean: data.totalRoomNotClean,
          totalRoomNotCleanWithStay: data.totalRoomNotCleanWithStay,
          totalRoomNotCleanWithOut: data.totalRoomNotCleanWithOut,
          totalRoomCleaned: data.totalRoomCleaned,
          totalRoomInspected: data.totalRoomInspected,
        })
        setGeneralNote({ announce: data.announce ?? '', announceEn: data.announceEn ?? '' })
      })
      .catch(e => {})
      .finally(() => {
        setIsLoading(false)
      })
  }

  const initialComponent = async () => {
    const checkList = (await fetchCleaningManagerSetting()) || []
    setStoredCheckList(checkList)
  }

  useEffect(() => {
    // set dayjs reflecting language settings
    setSelectedDate(dayjs())
  }, [i18n.language])

  useEffect(() => {
    fetchCleaningData(selectedDate)
  }, [selectedDate])

  const reloadRoomList = (fetchDate?: Dayjs) => {
    setIsOpenRoomDetailModal(false)
    fetchCleaningData(fetchDate || selectedDate)
    initialComponent()
  }

  useEffect(() => {
    if (Object.keys(rooms).length === 0) {
      return
    }
    setCollapsedTracking(
      Object.keys(rooms).reduce(function (result, item) {
        result[item] = collapsedTracking[item] !== undefined ? collapsedTracking[item] : false
        return result
      }, {}),
    )
  }, [rooms])

  useEffect(() => {
    if (Object.keys(collapsedTracking).length === 0) {
      return
    }
    localStorage.setItem(collapsedStorageKey, JSON.stringify(collapsedTracking))
  }, [collapsedTracking])

  useEffect(() => {
    initialComponent()
    setReloadCallback(() => {
      // intervalプロセスからstateの参照ができないため、setterから現値を参照する
      let originSelectedDate
      setSelectedDate(d => {
        originSelectedDate = d
        return d
      })
      reloadRoomList(originSelectedDate)
    }, 60000)
  }, [])

  const isSp = useMediaQuery('(max-width:599px)')
  const generalNoteContent = pickTranslatedText(generalNote.announce, generalNote.announceEn, i18n.language)

  return (
    <>
      <CleaningManagerContext.Provider
        value={{
          staffList: staffList,
          checkList: storedCheckList,
          selectedDate,
          reloadRoomList,
          setIsLoading,
        }}
      >
        {isSp ? (
          <>
            <IndexSp rooms={rooms} isLoading={isLoading} setSelectedDate={setSelectedDate} generalNoteContent={generalNoteContent} />
            <LoadingFull isLoading={isLoading} />
          </>
        ) : (
          <div css={[wholeContainerStyle, fontStyle]}>
            {isOpenRoomDetailModal && (
              <RoomDetail
                staffList={staffList}
                selectedDate={selectedDate}
                roomId={selectedRoomId}
                setIsOpenRoomDetailModal={setIsOpenRoomDetailModal}
                reloadRoomList={reloadRoomList}
              />
            )}
            <div className="mainColumn" css={[mainColumnStyle, { width: '100%' }]}>
              <Header title={t('Cleaning management')} reload={reloadRoomList} />
              <div css={mainContainerStyle}>
                <div css={headerCleaningRoomStyle}>
                  <div css={headerLeftCleaningRoomStyle}>
                    <DayStats selectedDate={selectedDate} setSelectedDate={setSelectedDate} roomStats={roomStats} />
                  </div>
                  <div css={headerRightCleaningRoomStyle}>
                    <GeneralNote generalNoteContent={generalNoteContent} />
                  </div>
                </div>
              </div>
              <StaffStatsList
                staffList={staffList}
                selectedDate={selectedDate}
                displayCleaningStaffId={displayCleaningStaffId}
                setDisplayCleaningStaffId={setDisplayCleaningStaffId}
              />
              <div css={secondMainContainerStyle}>
                <div css={cleaningMainStyle}>
                  <div css={headerWrapperStyle}>
                    <ListCleaningRoom
                      collapsedTracking={collapsedTracking}
                      setCollapsedTracking={setCollapsedTracking}
                      rooms={rooms}
                      selectedCleaningStaffId={displayCleaningStaffId}
                      setIsOpenRoomDetailModal={setIsOpenRoomDetailModal}
                      setSelectedRoomId={setSelectedRoomId}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <LoadingFull isLoading={isLoading} />
      </CleaningManagerContext.Provider>
    </>
  )
}

const fontStyle = css({
  '*': {
    fontFamily: 'Noto Sans JP !important',
  },
})

const headerLeftCleaningRoomStyle = css({
  width: '50%',
  background: '#fff',
  borderRadius: '5px',
  height: 152,
})
const headerRightCleaningRoomStyle = css({
  width: '50%',
  background: '#fff',
  borderRadius: '5px',
  minHeight: 152,
  marginLeft: '16px',
})

const headerCleaningRoomStyle = css({
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'space-between',
})

const secondMainContainerStyle = css({
  marginTop: 16,
  marginBottom: 24,
  padding: '0 24px 0 24px',
})

const cleaningMainStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  background: '#fff',
  justifyContent: 'space-between',
  borderRadius: '5px',
})

const headerWrapperStyle = css({
  width: '100%',
})

const staffListStyle = css({
  marginBlock: 16,
  fontSize: 12,
  paddingInline: 24,
  cursor: 'pointer',
  '.inner': {
    display: 'flex',
    background: '#fff',
    fontWeight: 'bold',
  },
  '.item': {
    padding: '8px 12px',
    borderRight: '1px solid #F2F2F2',
    '&.selected': {
      color: '#F2A40B',
    },
  },
  p: {
    textAlign: 'center',
    ':first-of-type': {
      marginBottom: 6,
    },
  },
})
